import React, { useCallback, useState, MouseEvent } from 'react';
import styled from '@emotion/styled';
import { Chip, Popover } from '@mui/material';
import { TimeClock } from '@mui/x-date-pickers/TimeClock';
import { useTranslation } from 'react-i18next';
import {
  FormRow,
  FormLabel,
  FormFieldWrapperWide,
  FormRowProps
} from '../form-input';
import { Moment } from 'moment';

const ChipsFieldWrapper = styled(FormFieldWrapperWide)`
  padding: ${p => p.theme.spacing(1, 1)};
  flex-wrap: wrap;
`;

export interface TimeClockFieldProps extends FormRowProps {
  label: string;
  info?: string;
  value?: number[];
  onChange: (value: number[]) => void;
  disabled?: boolean;
  limit?: number;
}

export function TimeClockField(props: TimeClockFieldProps) {
  const {
    label,
    info,
    value = [],
    onChange,
    disabled,
    limit = 4,
    ...formRowProps
  } = props;

  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openClock = useCallback(
    (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
    []
  );

  const closeClock = useCallback(() => setAnchorEl(null), []);

  const removeHour = useCallback(
    (hour: number) => {
      const next = value.filter(h => h !== hour);
      onChange(next);
    },
    [value, onChange]
  );

  const handleAddHour = useCallback(
    (moment: Moment | null) => {
      closeClock();

      if (!moment || value.includes(moment.hour()) || value.length >= limit) {
        return;
      }

      const next = value.concat(moment.hour()).sort((a, b) => a - b);

      onChange(next);
    },
    [value, onChange, closeClock, limit]
  );

  const asAMPM = (hour: number) => {
    const isPM = hour >= 12;
    const displayHour = isPM ? hour - 12 : hour;
    return `${displayHour || 12} ${isPM ? 'PM' : 'AM'}`;
  };

  return (
    <FormRow {...formRowProps}>
      <FormLabel info={info}>{label}</FormLabel>
      <ChipsFieldWrapper gap={1}>
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={closeClock}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center'
          }}
        >
          <TimeClock ampm={false} view="hours" onChange={handleAddHour} />
        </Popover>

        {value.map(hour => (
          <Chip
            key={hour}
            onDelete={() => removeHour(hour)}
            label={asAMPM(hour)}
          />
        ))}
        <Chip
          label={t('add')}
          color="primary"
          disabled={value.length >= limit}
          onClick={openClock}
        />
      </ChipsFieldWrapper>
    </FormRow>
  );
}
